import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BadgeDollarSign, BookOpen, CreditCard, ExternalLink, FolderTree, Inbox, Languages, LogOut,
  Menu, Settings, TicketPercent, Users, Wallet, X,
} from 'lucide-react';
import Brand from '../components/Brand';
import ModeToggle from '../components/ModeToggle';
import NotificationsBell from '../components/NotificationsBell';
import { authAPI } from '../lib/api';
import { clearSession, getUser } from '../lib/auth';
import { getLang, setLang, t } from '../lib/i18n';

// Back-office chrome (s134 redesign): the storefront's navy command sidebar with
// grouped nav + a white topbar. Role-aware: staff see Catalog / Students & sales
// / Platform; instructors get a "Teaching" group (their courses + earnings). The
// backend enforces the same split.
const STAFF_GROUPS = [
  ['Catalog', [
    { to: '/admin/courses', label: 'Courses', icon: BookOpen },
    { to: '/admin/categories', label: 'Categories', icon: FolderTree },
  ]],
  ['Students & sales', [
    { to: '/admin/enrollments', label: 'Enrollments', icon: Users },
    { to: '/admin/payments', label: 'Payments', icon: CreditCard },
    { to: '/admin/coupons', label: 'Coupons', icon: TicketPercent },
    { to: '/admin/payouts', label: 'Payouts', icon: BadgeDollarSign },
  ]],
  ['Platform', [
    { to: '/admin/applications', label: 'Applications', icon: Inbox },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ]],
];
const INSTRUCTOR_GROUPS = [
  ['Teaching', [
    { to: '/admin/courses', label: 'My courses', icon: BookOpen },
    { to: '/admin/earnings', label: 'Earnings', icon: Wallet },
  ]],
];
const TITLES = [
  [/^\/admin\/courses\/[^/]+/, 'Course editor'],
  [/^\/admin\/courses/, 'Courses'],
  [/^\/admin\/categories/, 'Categories'],
  [/^\/admin\/enrollments/, 'Enrollments'],
  [/^\/admin\/payments/, 'Payments'],
  [/^\/admin\/coupons/, 'Coupons'],
  [/^\/admin\/payouts/, 'Payouts'],
  [/^\/admin\/applications/, 'Applications'],
  [/^\/admin\/settings/, 'Settings'],
  [/^\/admin\/earnings/, 'Earnings'],
];
const ROLE_LABEL = { admin: 'Administrator', manager: 'Manager', instructor: 'Instructor' };

function NavItem({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-[11px] rounded-md py-[9px] pr-[11px] text-[13.5px] font-medium ${
          isActive
            ? 'border-l-[3px] border-lms-accent bg-white/10 pl-[11px] text-white'
            : 'border-l-[3px] border-transparent pl-[11px] text-[var(--lms-side-text)] hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <Icon size={17} />
      {t(label)}
    </NavLink>
  );
}

export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser() || {};
  const staff = ['admin', 'manager'].includes(user.lr);
  const groups = staff ? STAFF_GROUPS : INSTRUCTOR_GROUPS;
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch {
      /* stateless */
    }
    clearSession();
    navigate('/login', { replace: true });
  };

  const title = t((TITLES.find(([re]) => re.test(location.pathname)) || [null, ''])[1]);

  const sidebar = (
    <div className="flex h-full flex-col gap-1 px-3.5 py-[18px]">
      <div className="px-1.5 pb-1.5 text-white">
        <Brand size="md" linkToHome />
      </div>
      <span className="mx-1.5 mb-4 self-start rounded-md bg-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--lms-side-chip)]">
        {staff ? t('Back-office') : t('Instructor')}
      </span>
      <nav className="flex flex-col gap-0.5">
        {groups.map(([group, items], i) => (
          <div key={group} className="flex flex-col gap-0.5">
            <div className={`px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--lms-side-label)] ${i ? 'pt-[18px]' : 'pt-1'}`}>
              {t(group)}
            </div>
            {items.map((it) => <NavItem key={it.to} {...it} />)}
          </div>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-0.5 rounded-lg border border-white/10 bg-white/[0.06] p-3">
        <span className="text-[12.5px] font-semibold text-white">{user.name}</span>
        <span className="text-[11px] text-[var(--lms-side-text)]">{t(ROLE_LABEL[user.lr] || 'Instructor')}</span>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-lms-bg">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 bg-lms-sidebar lg:block">{sidebar}</aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <button type="button" aria-label={t('Close menu')} className="absolute inset-0 bg-black/45" onClick={() => setOpen(false)} />
          <aside className="relative h-full w-64 bg-lms-sidebar">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-[60px] items-center gap-3 border-b border-lms-border bg-lms-topbar px-4 sm:px-6">
          <button type="button" onClick={() => setOpen(true)} className="-ml-2.5 p-2.5 text-lms-muted hover:text-lms-text lg:hidden" aria-label={t('Menu')}>
            {open ? <X size={19} /> : <Menu size={19} />}
          </button>
          <span className="text-[15px] font-semibold text-lms-ink">{title}</span>
          <div className="ml-auto flex items-center gap-4">
            <ModeToggle />
            <NotificationsBell />
            <Link to="/" className="hidden items-center gap-1.5 text-[13px] font-medium text-lms-sub hover:text-lms-ink sm:flex">
              <ExternalLink size={15} />
              {t('View site')}
            </Link>
            <button
              type="button"
              onClick={() => setLang(getLang() === 'es' ? 'en' : 'es')}
              className="lms-hit flex items-center gap-1.5 text-[13px] font-medium text-lms-sub hover:text-lms-ink"
              title={t('Language')}
            >
              <Languages size={15} />
              {getLang().toUpperCase()}
            </button>
            <button type="button" onClick={signOut} className="lms-hit flex items-center gap-1.5 text-[13px] font-medium text-lms-sub hover:text-lms-ink">
              <LogOut size={15} />
              <span className="hidden sm:inline">{t('Sign out')}</span>
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-10 sm:py-8">
          <div className="mx-auto max-w-[1180px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
